import { Routes } from '@angular/router';
import { LISTING_PATH_PREFIXES } from '@padspin/listing-url';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    data: {
      homerContext:
        'the Padspin home / landlord marketing page explaining how Padspin leases units faster for a flat $800 success fee',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-landlord-page').then(
        (i) => i.MarketingLandlordPageComponent,
      ),
  },
  {
    path: 'tenant',
    redirectTo: 'rent',
  },
  {
    // QA fallback to the pre-Loveable landlord landing page — keep around
    // until the new landlord page has settled in production.
    path: 'landlord2',
    data: {
      homerContext:
        'the legacy landlord marketing landing page (a QA fallback kept during the new landlord page rollout)',
    },
    loadComponent: () =>
      import('@padspin/ui-landlord-landing-page').then(
        (i) => i.UiLandlordLandingPageComponent,
      ),
  },
  {
    // QA fallback to the pre-Loveable renter landing page — same reason.
    path: 'rent2',
    data: {
      homerContext:
        'the legacy renter marketing landing page (a QA fallback kept during the new renter page rollout)',
    },
    loadComponent: () =>
      import('@padspin/ui-renter-landing-page').then(
        (i) => i.RenterLandingPageComponent,
      ),
  },
  {
    path: 'rent',
    data: {
      homerContext:
        'the renter marketing page explaining Padspin has no broker fees, honest listings, and fair data-backed pricing',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-renter-page').then(
        (i) => i.MarketingRenterPageComponent,
      ),
  },
  // Old preview paths kept as permanent redirects so any external links
  // landed during the Loveable rollout don't 404.
  {
    path: 'landlord-preview',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'renter-preview',
    redirectTo: 'rent',
    pathMatch: 'full',
  },
  {
    path: 'pricing',
    data: {
      homerContext:
        'the pricing page — Padspin charges landlords a flat $800 only if the unit rents ($350 + $50/renter on renewal)',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-pricing-page').then(
        (i) => i.MarketingPricingPageComponent,
      ),
  },
  {
    path: 'how-it-works',
    data: {
      homerContext:
        'the how-it-works page explaining the Padspin rental process',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-how-it-works-page').then(
        (i) => i.MarketingHowItWorksPageComponent,
      ),
  },
  {
    path: 'cities',
    data: {
      homerContext:
        'the cities marketing page listing the markets where Padspin operates',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-cities-page').then(
        (i) => i.MarketingCitiesPageComponent,
      ),
  },
  {
    path: 'press',
    data: {
      homerContext:
        'the press marketing page with Padspin media coverage and press resources',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-press-page').then(
        (i) => i.MarketingPressPageComponent,
      ),
  },
  {
    path: 'fair-housing',
    data: {
      homerContext:
        "the fair-housing marketing page describing Padspin's fair-housing commitments",
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-fair-housing-page').then(
        (i) => i.MarketingFairHousingPageComponent,
      ),
  },
  {
    path: 'talk-to-sales',
    data: {
      homerContext:
        'the talk-to-sales marketing page where a landlord can contact the Padspin sales team',
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-talk-to-sales-page').then(
        (i) => i.MarketingTalkToSalesPageComponent,
      ),
  },
  {
    path: 'landlord',
    redirectTo: '/',
  },
  {
    path: 'm',
    data: {
      homerContext:
        "the Account Manager dashboard (an AM's internal workspace for managing listings, leads, and tasks)",
      homerFabPosition: 'am-dashboard',
    },
    loadChildren: () =>
      import('@padspin/ui-account-manager-dashboard/routes').then(
        (i) => i.accountManagerDashboardRoutes,
      ),
  },
  {
    path: 'agents',
    data: {
      homerContext:
        'the Padspin for Agents marketing/recruiting page for prospective Account Managers',
    },
    loadChildren: () =>
      import('@padspin/ui-marketing-agents-page/routes').then(
        (i) => i.agentsPageRoutes,
      ),
  },
  {
    path: 'school',
    data: {
      homerContext:
        'Padspin School, the training program and quizzes for prospective Account Managers',
    },
    loadChildren: () =>
      import('@padspin/ui-school').then((i) => i.schoolRoutes),
  },
  {
    path: 'property-management',
    data: {
      homerContext:
        "the property-management marketing page pitching Padspin's property-management services to landlords",
    },
    loadComponent: () =>
      import('@padspin/ui-marketing-property-management-page').then(
        (i) => i.MarketingPropertyManagementPageComponent,
      ),
  },
  {
    path: 'manage',
    redirectTo: 'agents',
    pathMatch: 'full',
  },
  {
    path: 'account-manager-onboarding',
    data: {
      homerContext:
        'the Account Manager onboarding flow for newly accepted AMs',
    },
    loadChildren: () =>
      import('@padspin/ui-account-manager-onboarding/routes').then(
        (i) => i.accountManagerOnboardingRoutes,
      ),
  },
  {
    path: 'register',
    loadChildren: () =>
      import('@padspin/ui-register').then((m) => m.RegisterPageModule),
    data: {
      register: true,
      homerContext: 'the account registration page',
    },
  },
  {
    path: 'login',
    loadChildren: () =>
      import('@padspin/ui-login').then((m) => m.LoginPageModule),
    data: {
      login: true,
      homerContext: 'the login page',
    },
  },
  {
    path: 'post-pad',
    data: {
      homerContext:
        'the Post-a-Pad flow where a landlord lists a unit with Padspin',
    },
    loadChildren: () => import('@padspin/ui-post-a-pad/routes'),
  },
  {
    path: 'criteria',
    data: {
      homerContext:
        'the tenant-criteria page where a renter sets their apartment search criteria',
    },
    loadComponent: () =>
      import('@padspin/ui-tenant-criteria').then(
        (i) => i.TenantCriteriaComponent,
      ),
  },
  {
    path: 'blog',
    data: { homerContext: 'the Padspin blog index' },
    loadChildren: () =>
      import('@padspin/ui-blog').then((m) => m.BlogListViewPageModule),
  },
  {
    path: 'blog/:blogPostId',
    data: { homerContext: 'a Padspin blog post' },
    loadComponent: () =>
      import('@padspin/ui-blog').then((i) => i.BlogEntryViewPageComponent),
  },
  {
    path: 'dashboard',
    data: {
      homerContext:
        "the Padspin account dashboard, with renter, landlord, Account Manager and admin sections depending on the user's roles",
    },
    loadChildren: () =>
      import('@padspin/ui-admin-dashboard').then((m) => m.dashboardRoutes),
  },
  {
    path: 'frequently-asked-questions',
    data: { homerContext: 'the frequently-asked-questions (FAQ) page' },
    loadChildren: () => import('@padspin/ui-faq').then((m) => m.FaqModule),
  },
  {
    path: 'faq',
    data: { homerContext: 'the frequently-asked-questions (FAQ) page' },
    loadChildren: () => import('@padspin/ui-faq').then((m) => m.FaqModule),
  },
  {
    path: 'terms-of-service',
    data: { homerContext: 'the Terms of Service page' },
    loadChildren: () =>
      import('@padspin/ui-terms-of-service').then(
        (m) => m.TermsOfServiceModule,
      ),
  },
  {
    path: 'terms',
    data: { homerContext: 'the Terms of Service page' },
    loadChildren: () =>
      import('@padspin/ui-terms-of-service').then(
        (m) => m.TermsOfServiceModule,
      ),
  },
  {
    path: 'privacy-policy',
    data: { homerContext: 'the Privacy Policy page' },
    loadComponent: () =>
      import('@padspin/ui-privacy-policy').then(
        (m) => m.PrivacyPolicyPageComponent,
      ),
  },
  {
    path: 'sms-privacy-policy',
    data: { homerContext: 'the SMS privacy policy page' },
    loadComponent: () =>
      import('@padspin/ui-sms-privacy-policy').then(
        (m) => m.SmsPrivacyPolicyPageComponent,
      ),
  },
  {
    path: 'sms-terms-and-conditions',
    data: { homerContext: 'the SMS terms and conditions page' },
    loadComponent: () =>
      import('@padspin/ui-sms-terms').then((m) => m.SmsTermsPageComponent),
  },
  {
    path: 'sms-consent',
    data: { homerContext: 'the SMS consent page' },
    loadComponent: () =>
      import('@padspin/ui-sms-consent').then((m) => m.SmsConsentPageComponent),
  },
  {
    path: 'owner-onboarding',
    data: {
      homerContext:
        'the page a landlord opens from the onboarding link their account manager sent, to put their apartment on Padspin',
    },
    loadChildren: () =>
      import('@padspin/ui-owner-onboarding').then(
        (m) => m.ownerOnboardingRoutes,
      ),
  },
  {
    path: 'search',
    data: { homerContext: 'the apartment search / listings results page' },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  },
  {
    path: 'listings',
    data: { homerContext: 'the apartment listings page' },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  },
  {
    path: 'l',
    data: { homerContext: 'an individual apartment listing detail page' },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  },
  {
    path: 'no-fee-apartments-for-rent',
    data: {
      homerContext:
        'the no-fee apartments search/listings page (an SEO landing into apartment results)',
    },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  },
  {
    path: 'l-legacy',
    data: {
      homerContext:
        'the previous designs of the apartment search map and of an individual apartment listing detail page, kept for comparison',
    },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.listingLegacyRoutes),
  },
  ...LISTING_PATH_PREFIXES.map((path) => ({
    path,
    data: { homerContext: 'an individual apartment listing detail page' },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  })),
  {
    path: 'matches',
    data: {
      homerContext:
        "the renter's matches page (apartments matched to their saved criteria)",
    },
    loadChildren: () =>
      import('@padspin/ui-matches-page').then((m) => m.matchesPageRoutes),
  },
  {
    path: 'book:bookingUUID',
    data: {
      homerContext: 'the tour-booking page for scheduling an apartment viewing',
    },
    loadComponent: () =>
      import('@padspin/ui-booking').then((i) => i.BookingUUIDPageComponent),
  },
  {
    path: 'post-pad',
    data: {
      homerContext:
        'the Post-a-Pad flow where a landlord lists a unit with Padspin',
    },
    loadChildren: () => import('@padspin/ui-post-a-pad/routes'),
  },
  {
    path: 'post-a-pad',
    redirectTo: 'post-pad',
  },
  {
    path: 'agreement',
    redirectTo: 'post-pad',
  },
  {
    path: 'sign-agreement',
    redirectTo: 'post-pad',
  },
  {
    path: 'forgot',
    data: {
      sendRequestEmail: true,
      homerContext: 'the forgot-password page',
    },
    loadChildren: () =>
      import('@padspin/ui-forgot-page').then((m) => m.ForgotPageModule),
  },
  {
    path: '__/auth/action',
    data: {
      homerContext:
        'the email action handler page (verifying email links and completing password resets)',
    },
    loadChildren: () => import('@padspin/ui-email').then((m) => m.EmailModule),
  },
  {
    path: 'sign-in/link',
    data: {
      homerContext: 'the page that completes an emailed sign-in link',
    },
    loadChildren: () =>
      import('@padspin/ui-sign-in-link').then((m) => m.signInLinkRoutes),
  },
  {
    path: 'onboarding',
    redirectTo: 'login',
  },
  {
    path: 'pitch-deck',
    data: { homerContext: 'the Padspin investor pitch deck' },
    loadChildren: () =>
      import('@padspin/ui-pitch-deck-page').then((m) => m.PitchDeckPageModule),
  },
  {
    path: 'rental-app',
    data: {
      homerContext:
        'the renter rental-application flow (paying, filling out, and submitting an application)',
    },
    loadChildren: () =>
      import('@padspin/ui-rental-application-routes').then(
        (i) => i.applicationPageRoutes,
      ),
  },
  // Rental application redesign — new single-doc model. Mounted alongside
  // the legacy `/rental-app` routes above (left unchanged). When the
  // switchover happens, listing "Apply" links point at the new
  // `/rental-application/:slug` path; in-flight legacy applications stay
  // on `/rental-app/...`.
  {
    path: '',
    data: {
      homerContext:
        'the renter rental-application flow (new single-document application model)',
    },
    loadChildren: () =>
      import('@padspin/ui-rental-application-group').then(
        (i) => i.rentalApplicationGroupRoutes,
      ),
  },
  {
    path: 'move',
    data: {
      homerContext:
        'the move / relocation flow for a renter coordinating their move',
    },
    loadChildren: () =>
      import('@padspin/ui-move/routes').then((i) => i.moveRoutes),
  },
  {
    path: 'moves',
    redirectTo: 'move',
  },
  {
    path: 'move-form',
    data: { homerContext: 'the move details form' },
    loadChildren: () =>
      import('@padspin/ui-move').then((m) => m.MoveFormPageModule),
  },
  {
    path: 'comp-report',
    loadChildren: () =>
      import('@padspin/ui-comp-reports/routes').then(
        (i) => i.compReportsPageRoutes,
      ),
  },
  {
    path: 'renew',
    data: {
      homerContext:
        'the landlord renewal / payment page for renewing a listing with Padspin',
    },
    loadChildren: () =>
      import('@padspin/ui-payment/landlord').then(
        (i) => i.collectPaymentInfoRoutes,
      ),
  },
  {
    path: 'renewal',
    redirectTo: 'renew',
  },
  {
    path: 'checkout',
    redirectTo: 'renew',
  },
  {
    path: 'not-found',
    data: { homerContext: 'the 404 not-found page' },
    loadChildren: () =>
      import('@padspin/ui-not-found-page').then((m) => m.NotFoundModule),
  },
  {
    path: '**',
    data: { homerContext: 'the 404 page for an unmatched URL' },
    loadChildren: () =>
      import('@padspin/ui-not-found-page').then((m) => m.NotFoundModule),
  },
] satisfies Routes;
